'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Download, FileText, ArrowLeft, CheckCircle2, ShieldCheck, BookOpen, User, Phone, Mail, Briefcase, AlertCircle } from 'lucide-react';



const documentChecklist = [
  'Birth Certificate',
  'Tax Registration Number (TRN)',
  'Government-issued ID (National ID / Passport / Driver\'s Licence)',
  'Academic Certificates (CXC, CAPE, Diplomas, Degrees)',
  'Professional Certificates (if applicable)',
  'Resume / Curriculum Vitae (CV)',
  'Job Letter or Proof of Employment (if applicable)',
  'Other Supporting Documents (if requested)',
];

const FULL_DECLARATION_CLAUSES = [
  'I confirm that the information provided is accurate and complete.',
  'I understand that missing, unclear, false, or incorrect information may delay my registration verification, assessment processing, RFA submission, or certification.',
  'I understand that the documents submitted will be used to complete my student file and support registration, assessment, verification, RFA processing, certification, reporting, and related administrative processes.',
  'I give permission for Ziva Niara Solutions to collect, store, process, and share my biodata, contact information, programme information, and supporting documents with HEART Trust/NSTA, NCTVET, and the selected Accredited Training Organization, where necessary.',
  'I understand that my information will only be used and shared for official training, registration, assessment, certification, verification, reporting, and administrative purposes connected to my programme.',
  'I understand that Ziva Niara Solutions may contact me by phone, WhatsApp, email, or other official communication channels about my programme.',
  "I give permission for my uploaded documents to be stored securely in Ziva Niara Solutions' Google Workspace Shared Drive and accessed only by authorised personnel.",
  'I understand that my documents will be retained only for the period required for registration, assessment, certification, audit, reporting, and institutional recordkeeping.',
];

export default function DownloadFormClient() {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Load logo image
      const logoImg = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = '/assets/images/logo_1_1_-1778033781070.jpeg';
      });

      const canvas = document.createElement('canvas');
      canvas.width = logoImg.naturalWidth;
      canvas.height = logoImg.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx!.drawImage(logoImg, 0, 0);
      const logoDataUrl = canvas.toDataURL('image/jpeg');

      const pageW = 210;
      const pageH = 297;
      const margin = 18;
      const contentW = pageW - margin * 2;
      let y = margin;

      const addPage = () => {
        doc.addPage();
        y = margin;
      };

      const checkPageBreak = (needed: number) => {
        if (y + needed > pageH - margin) addPage();
      };

      // ── HEADER ──
      // Purple header bar
      doc.setFillColor(107, 33, 168);
      doc.rect(0, 0, pageW, 38, 'F');

      // Logo image
      doc.addImage(logoDataUrl, 'JPEG', margin, 6, 26, 26);

      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('ZIVA NIARA SOLUTIONS', margin + 32, 15);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text('Ziva DocuSecure — Student Biodata & Registration Form', margin + 32, 22);
      doc.setFontSize(8);
      doc.text('docusecure.myzivasol.com  |  876-277-3952  |  admin@zivasol.com', margin + 32, 29);

      y = 46;

      // Contact info line
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.text('Suite 16, 4-5 Lismore Avenue, Kingston, St. Andrew, Jamaica  |  www.zivasol.com', margin, y);
      y += 5;

      // Form reference line
      doc.setFontSize(8);
      doc.text('Form Ref: ZNS-BIO-2026 | Version 2.0 | For official use only', margin, y);
      doc.text('Date Submitted: ___________________', pageW - margin, y, { align: 'right' });
      y += 8;

      // Divider
      doc.setDrawColor(107, 33, 168);
      doc.setLineWidth(0.8);
      doc.line(margin, y, pageW - margin, y);
      y += 6;

      // ── SECTION HELPER ──
      const sectionHeader = (title: string) => {
        checkPageBreak(12);
        doc.setFillColor(245, 240, 255);
        doc.rect(margin, y, contentW, 8, 'F');
        doc.setDrawColor(107, 33, 168);
        doc.setLineWidth(0.3);
        doc.rect(margin, y, contentW, 8, 'S');
        doc.setTextColor(107, 33, 168);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(title.toUpperCase(), margin + 3, y + 5.5);
        y += 11;
      };

      const fieldRow = (label: string, wide = false) => {
        checkPageBreak(12);
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(label + ':', margin, y + 4);
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.3);
        const lineStart = wide ? margin : margin + 45;
        const lineEnd = pageW - margin;
        doc.line(lineStart, y + 5, lineEnd, y + 5);
        y += 10;
      };

      const twoFieldRow = (label1: string, label2: string) => {
        checkPageBreak(12);
        const half = contentW / 2 - 3;
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(label1 + ':', margin, y + 4);
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.3);
        doc.line(margin + 32, y + 5, margin + half, y + 5);
        doc.text(label2 + ':', margin + half + 6, y + 4);
        doc.line(margin + half + 6 + 32, y + 5, pageW - margin, y + 5);
        y += 10;
      };

      // ── SECTION 1: PROGRAMME INFO ──
      sectionHeader('Section 1 — Programme Information');
      fieldRow('Programme Applied For');
      twoFieldRow('Intake Period', 'Class / Cohort Name');
      twoFieldRow('Application Date', 'TRN');
      y += 2;

      // ── SECTION 2: PERSONAL DETAILS ──
      sectionHeader('Section 2 — Personal Details');
      twoFieldRow('First Name', 'Middle Name');
      fieldRow('Last Name');
      twoFieldRow('Date of Birth (DD/MM/YYYY)', 'Gender (Male / Female)');
      twoFieldRow('Phone Number', 'WhatsApp Number');
      fieldRow('Email Address');
      fieldRow('Home Address');
      fieldRow('Parish');
      y += 2;

      // ── SECTION 3: EMERGENCY CONTACT ──
      sectionHeader('Section 3 — Emergency Contact');
      fieldRow('Emergency Contact Name');
      twoFieldRow('Emergency Contact Number', 'Relationship');
      y += 2;

      // ── SECTION 4: EDUCATION & EMPLOYMENT ──
      sectionHeader('Section 4 — Education & Employment');
      fieldRow('Highest Level of Education');
      twoFieldRow('Employment Status', 'Current Job Title');
      fieldRow('Organisation / Employer');
      twoFieldRow('Industry Area', 'Years of Experience');
      y += 2;

      // ── SECTION 5: PROGRAMME SUPPORT STATEMENT ──
      sectionHeader('Section 5 — Programme Support Statement');
      checkPageBreak(30);
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text('Briefly explain how the programme will support you to assist, complete, or transition in your current or future role:', margin, y);
      y += 5;
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      for (let i = 0; i < 5; i++) {
        checkPageBreak(8);
        doc.line(margin, y + 5, pageW - margin, y + 5);
        y += 8;
      }
      y += 4;

      // ── SECTION 6: DOCUMENT CHECKLIST ──
      sectionHeader('Section 6 — Required Document Checklist');
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'italic');
      doc.text('Please tick each document you are submitting with this form:', margin, y);
      y += 6;

      documentChecklist.forEach((doc_item, idx) => {
        checkPageBreak(8);
        doc.setDrawColor(107, 33, 168);
        doc.setLineWidth(0.4);
        doc.rect(margin, y, 4, 4, 'S');
        doc.setTextColor(40, 40, 40);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`${idx + 1}. ${doc_item}`, margin + 7, y + 3.2);
        y += 7;
      });
      y += 4;

      // ── SECTION 7: DECLARATION & CONSENT ──
      checkPageBreak(20);
      sectionHeader('Section 7 — Declaration & Consent');

      // Data sharing notice box
      doc.setFillColor(235, 245, 255);
      doc.setDrawColor(59, 130, 246);
      doc.setLineWidth(0.4);
      const noticeText = 'Data Sharing Notice: Your biodata, contact information, programme information, and supporting documents may be shared with HEART Trust/NSTA, NCTVET, and the selected Accredited Training Organization (ATO) where necessary. Documents will be stored securely in Ziva Niara Solutions\' Google Workspace Shared Drive and accessed only by authorised personnel.';
      const splitNotice = doc.splitTextToSize(noticeText, contentW - 6);
      doc.rect(margin, y, contentW, splitNotice.length * 4.5 + 6, 'FD');
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(splitNotice, margin + 3, y + 5);
      y += splitNotice.length * 4.5 + 10;

      // Declaration clauses
      FULL_DECLARATION_CLAUSES.forEach((clause, idx) => {
        checkPageBreak(14);
        doc.setFillColor(255, 251, 235);
        doc.setDrawColor(217, 119, 6);
        doc.setLineWidth(0.3);
        const splitClause = doc.splitTextToSize(`${idx + 1}. ${clause}`, contentW - 8);
        doc.rect(margin, y, contentW, splitClause.length * 4.5 + 5, 'FD');
        doc.setTextColor(60, 40, 0);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.text(splitClause, margin + 4, y + 4.5);
        y += splitClause.length * 4.5 + 8;
      });

      // Consent tick box
      checkPageBreak(14);
      doc.setDrawColor(107, 33, 168);
      doc.setLineWidth(0.5);
      doc.rect(margin, y, 5, 5, 'S');
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('I have read, understood, and accept all of the above declaration clauses.', margin + 8, y + 4);
      y += 10;

      // Signature section
      checkPageBreak(40);
      twoFieldRow('Student Full Name (Print)', 'Date Signed');
      twoFieldRow('Time Signed', 'Consent Status');
      checkPageBreak(20);
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Student Signature:', margin, y + 4);
      doc.setDrawColor(107, 33, 168);
      doc.setLineWidth(0.5);
      doc.line(margin + 38, y + 5, margin + 38 + 80, y + 5);
      y += 14;

      // ── SECTION 8: ADMIN / REVIEWER SECTION ──
      checkPageBreak(50);
      sectionHeader('Section 8 — For Admin / Reviewer Use Only');
      doc.setFillColor(248, 248, 248);
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.rect(margin, y, contentW, 45, 'FD');
      const adminY = y + 5;
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');

      doc.text('Received By:', margin + 3, adminY + 4);
      doc.setDrawColor(180, 180, 180);
      doc.line(margin + 28, adminY + 5, margin + 28 + 60, adminY + 5);
      doc.text('Date Received:', margin + 3 + 95, adminY + 4);
      doc.line(margin + 3 + 95 + 28, adminY + 5, pageW - margin - 3, adminY + 5);
      doc.text('Reviewed By:', margin + 3, adminY + 14);
      doc.line(margin + 28, adminY + 15, margin + 28 + 60, adminY + 15);
      doc.text('Date Reviewed:', margin + 3 + 95, adminY + 14);
      doc.line(margin + 3 + 95 + 28, adminY + 15, pageW - margin - 3, adminY + 15);
      doc.text('Verification Status:', margin + 3, adminY + 24);
      doc.line(margin + 38, adminY + 25, margin + 38 + 50, adminY + 25);
      doc.text('Admin Notes:', margin + 3, adminY + 34);
      doc.line(margin + 28, adminY + 35, pageW - margin - 3, adminY + 35);
      y += 50;

      // ── FOOTER ──
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(245, 240, 255);
        doc.rect(0, pageH - 14, pageW, 14, 'F');
        doc.setTextColor(107, 33, 168);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(
          'Ziva Niara Solutions  |  Ziva DocuSecure  |  docusecure.myzivasol.com  |  876-277-3952  |  admin@zivasol.com  |  docusecure@myzivasol.com',
          pageW / 2,
          pageH - 8,
          { align: 'center' }
        );
        doc.text(
          'Suite 16, 4-5 Lismore Avenue, Kingston, St. Andrew, Jamaica  |  www.zivasol.com  |  © 2026 All Rights Reserved',
          pageW / 2,
          pageH - 4,
          { align: 'center' }
        );
        doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 8, { align: 'right' });
      }

      doc.save('Ziva_DocuSecure_Blank_Registration_Form.pdf');
      setDownloaded(true);
    } catch (err) {
      console.error('PDF generation error:', err);
    }
    setDownloading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-white py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors mb-6">
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/20 flex-shrink-0">
              <Image src="/assets/images/logo_1_1_-1778033781070.jpeg" alt="Ziva Niara Solutions logo" width={56} height={56} className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Download Registration Form</h1>
              <p className="text-white/70 text-sm">Ziva Niara Solutions — Student Biodata & Registration Form</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Download card */}
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <FileText size={22} className="text-primary" />
            </div>
            <h2 className="text-lg font-bold text-foreground mb-2">Blank Registration Form</h2>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              Download the official Ziva Niara Solutions student biodata and registration form as a PDF. This blank form includes all required fields, the document checklist, full consent declaration, and admin/reviewer section.
            </p>

            <div className="space-y-2 mb-6">
              {[
                'Ziva Niara Solutions heading & logo',
                'All student biodata fields',
                'Programme, intake period & cohort information',
                'Required document checklist (8 documents)',
                'Full consent & declaration statement',
                'Signature, date & time section',
                'Admin / reviewer section',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle2 size={13} className="text-green-600 flex-shrink-0" />
                  <span className="text-xs text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="btn-primary w-full py-3"
            >
              {downloading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Generating PDF...
                </span>
              ) : downloaded ? (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle2 size={16} /> Downloaded Successfully
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Download size={16} /> Download Blank Form (PDF)
                </span>
              )}
            </button>

            {downloaded && (
              <p className="text-xs text-green-600 text-center mt-2 flex items-center justify-center gap-1">
                <CheckCircle2 size={11} /> Form saved as PDF to your downloads folder
              </p>
            )}

            <div className="mt-4 flex items-start gap-2 p-3 bg-secondary/40 border border-primary/15 rounded-xl">
              <ShieldCheck size={13} className="text-primary flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                This blank form is publicly available. Completed signed forms are only accessible to the submitting student and authorised admin users.
              </p>
            </div>
          </div>

          {/* Form preview / info */}
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-bold text-foreground text-sm mb-3 flex items-center gap-2">
                <BookOpen size={15} className="text-primary" /> Form Sections
              </h3>
              <div className="space-y-2">
                {[
                  { num: '1', title: 'Programme Information', icon: BookOpen },
                  { num: '2', title: 'Personal Details', icon: User },
                  { num: '3', title: 'Emergency Contact', icon: Phone },
                  { num: '4', title: 'Education & Employment', icon: Briefcase },
                  { num: '5', title: 'Programme Support Statement', icon: FileText },
                  { num: '6', title: 'Required Document Checklist', icon: CheckCircle2 },
                  { num: '7', title: 'Declaration & Consent', icon: ShieldCheck },
                  { num: '8', title: 'Admin / Reviewer Section', icon: AlertCircle },
                ].map((s) => {
                  const SIcon = s.icon;
                  return (
                    <div key={s.num} className="flex items-center gap-3 py-1.5 border-b border-border last:border-0">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-primary">{s.num}</span>
                      </div>
                      <SIcon size={13} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-foreground">{s.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-bold text-foreground text-sm mb-3 flex items-center gap-2">
                <Mail size={15} className="text-primary" /> Already Registered?
              </h3>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                If you already have an account, sign in to complete your biodata form online and upload your documents directly.
              </p>
              <div className="space-y-2">
                <Link href="/student-sign-in" className="btn-primary w-full text-sm">Student Sign In</Link>
                <Link href="/student-register" className="btn-outline w-full text-sm">Create New Account</Link>
              </div>
            </div>

            {/* Contact info card */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-bold text-foreground text-sm mb-3 flex items-center gap-2">
                <Phone size={15} className="text-primary" /> Contact Ziva Niara Solutions
              </h3>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <p>📞 <a href="tel:+18762773952" className="text-primary font-semibold hover:underline">876-277-3952</a></p>
                <p>✉️ <a href="mailto:admin@zivasol.com" className="text-primary font-semibold hover:underline">admin@zivasol.com</a></p>
                <p>✉️ <a href="mailto:docusecure@myzivasol.com" className="text-primary font-semibold hover:underline">docusecure@myzivasol.com</a></p>
                <p>🌐 <a href="https://www.zivasol.com" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">www.zivasol.com</a></p>
                <p>📍 Suite 16, 4-5 Lismore Avenue, Kingston, St. Andrew, Jamaica</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
